/**
 * Phase 66 Plan 02 Task 3 — Login overlay with react-hook-form + zod.
 * Phase 69 Plan 06 — extended with conditional `totp_code` field +
 *                    account_disabled banner. UI-SPEC §6.7.
 *
 * Preserves the Phase 65 markup contract verbatim — see PATTERNS.md
 * §"Selectors that MUST survive" / 66-TESTID-CONTRACT.md row A1:
 *
 *   #login-overlay   .login-overlay   data-testid="login-overlay"
 *   #login-form      .login-form      data-testid="login-form"
 *   #login-email                      data-testid="login-email"
 *   #login-password                   data-testid="login-password"
 *   #login-error                      data-testid="login-error"
 *   .login-logo                       (Phase 65 D-04 brand asset)
 *   .btn .btn-primary                 (Phase 65 button contract)
 *
 * Validation: zod schema (email + non-empty password + optional 6-digit
 * totp_code). On submit error, the rendered #login-error slot surfaces
 * the backend `body.detail` — which is exactly what api/client.ts
 * produces for 401 responses thanks to `skipAuthLogout` on /auth/login.
 *
 * 2FA state machine (Phase 69 Plan 06 — body.detail contract from Plan 03):
 *   - first submit (email + password) → POST /auth/login {email, password}
 *     - 200 access_token                → AppShell rerenders, done
 *     - 401 detail="Invalid credentials"→ submitError slot (#login-error)
 *     - 401 detail="totp_required"      → setNeedsTotp(true); reveal totp_code field; preserve form state
 *     - 401 detail="account_disabled"   → setAccountDisabled(true) banner
 *   - second submit (email + password + totp_code) — needsTotp=true:
 *     - 200                             → done
 *     - 401 detail="totp_invalid"       → submitError slot with i18n auth.login.totp.errorInvalid; field stays
 *     - 401 detail="account_disabled"   → banner (deactivated mid-flow)
 *   - 500 detail="2fa_misconfigured"    → fall-through to submitError slot with generic copy
 *
 * Threat model:
 *   T-66-02-04 (open redirect via ?next=) — no `next` query parameter is
 *     supported. After login, the AppShell rerenders (user becomes
 *     non-null) and the existing route resolves; deep-links survive.
 *   T-69-06-06 (account_disabled enumeration) — explicit distinct copy
 *     for account_disabled vs invalid_credentials is a documented
 *     accept-trade-off (CONTEXT D-DELETE-01, Plan 03 threat T-69-03-07).
 *
 * @see PATTERNS.md §`src/auth/LoginForm.tsx`
 * @see RESEARCH.md §Pattern 7 — react-hook-form + zod
 * @see RESEARCH.md §Pattern 4 — login extension (Plan 06)
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/auth/AuthProvider";

const schema = z.object({
  email: z.string().email("Inserisci un indirizzo email valido"),
  password: z.string().min(1, "Password obbligatoria"),
  // Optional — only validated when needsTotp=true (zod cannot read React
  // state, so we validate the format here and gate "required" at submit).
  totp_code: z
    .string()
    .regex(/^\d{6}$/u, "Codice 2FA: 6 cifre")
    .or(z.literal(""))
    .optional(),
});

type LoginFormValues = z.infer<typeof schema>;

interface ApiErrLike {
  status?: number;
  body?: { detail?: string };
}

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [accountDisabled, setAccountDisabled] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "", totp_code: "" },
  });

  async function onSubmit(values: LoginFormValues) {
    setSubmitError(null);
    setAccountDisabled(false);
    try {
      await login(
        values.email,
        values.password,
        // Only send totp_code on the second submit; an empty string is
        // never meaningful to the backend (zod regex would have rejected
        // a partial 1-5 digit value before reaching here).
        needsTotp && values.totp_code ? values.totp_code : undefined,
      );
      // AppShell rerenders when useUser() becomes non-null — no manual nav.
    } catch (err) {
      const e = err as Error & ApiErrLike;
      const detail = e.body?.detail;
      if (detail === "totp_required") {
        // Backend says: credentials valid, but this user has TOTP enabled.
        // Reveal the totp_code field and keep form state intact (email +
        // password were correct and must NOT be cleared).
        setNeedsTotp(true);
        return;
      }
      if (detail === "totp_invalid") {
        setSubmitError(t("auth.login.totp.errorInvalid"));
        return;
      }
      if (detail === "account_disabled") {
        setAccountDisabled(true);
        return;
      }
      setSubmitError(e.message || t("auth.login.failed"));
    }
  }

  // Surface either the per-field zod error OR the backend submit error
  // through the same #login-error slot. Phase 65 only had one error slot;
  // we preserve the semantics (one visible message at a time).
  const displayedError =
    errors.email?.message ??
    errors.password?.message ??
    (needsTotp ? errors.totp_code?.message : undefined) ??
    submitError;

  return (
    <div
      id="login-overlay"
      data-testid="login-overlay"
      className="login-overlay fixed inset-0 flex items-center justify-center bg-bg z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-form-title"
    >
      <form
        id="login-form"
        data-testid="login-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className="login-form bg-surface p-8 rounded-lg shadow-deep w-full max-w-sm flex flex-col gap-4"
      >
        <img
          src="/img/eurven/logo_eurven_black.png"
          alt="Eurven"
          className="login-logo h-10 mx-auto"
        />
        <h2
          id="login-form-title"
          className="text-lg font-semibold text-center"
        >
          {t("auth.login.title")}
        </h2>

        {accountDisabled ? (
          <div
            data-testid="login-account-disabled-banner"
            role="alert"
            className="alert alert-danger text-sm text-[color:var(--color-danger)] bg-[color:var(--color-danger-bg)] px-3 py-2 rounded"
          >
            {t("auth.login.account_disabled")}
          </div>
        ) : null}

        <div className="form-group flex flex-col gap-1">
          <label htmlFor="login-email" className="text-sm">
            {t("auth.login.email")}
          </label>
          <input
            id="login-email"
            data-testid="login-email"
            type="email"
            autoComplete="username"
            aria-invalid={errors.email ? "true" : "false"}
            className="form-input px-3 py-2 border border-[color:var(--color-border)] rounded"
            {...register("email")}
          />
        </div>

        <div className="form-group flex flex-col gap-1">
          <label htmlFor="login-password" className="text-sm">
            {t("auth.login.password")}
          </label>
          <input
            id="login-password"
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            aria-invalid={errors.password ? "true" : "false"}
            className="form-input px-3 py-2 border border-[color:var(--color-border)] rounded"
            {...register("password")}
          />
        </div>

        {needsTotp ? (
          <div
            data-testid="login-totp-field"
            className="form-group flex flex-col gap-1"
          >
            <label htmlFor="login-totp-code" className="text-sm">
              {t("auth.login.totp.label")}
            </label>
            <input
              id="login-totp-code"
              data-testid="login-totp-input"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              placeholder={t("auth.login.totp.placeholder")}
              aria-invalid={errors.totp_code ? "true" : "false"}
              className="form-input px-3 py-2 border border-[color:var(--color-border)] rounded font-mono tracking-widest text-center"
              {...register("totp_code")}
            />
            <p className="text-xs text-[color:var(--color-text-muted)]">
              {t("auth.login.totp.required")}
            </p>
          </div>
        ) : null}

        {displayedError ? (
          <div
            id="login-error"
            data-testid="login-error"
            className="alert alert-danger text-sm text-[color:var(--color-danger)] bg-[color:var(--color-danger-bg)] px-3 py-2 rounded"
            role="alert"
          >
            {displayedError}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn btn-primary bg-primary text-white px-4 py-2 rounded hover:bg-[color:var(--color-primary-hover)] disabled:opacity-60"
        >
          {isSubmitting ? t("common.loading") : t("auth.login.submit")}
        </button>
      </form>
    </div>
  );
}
